import React, { useState, useRef, useEffect } from 'react';

/**
 * HazardAutocomplete Component
 *
 * A searchable dropdown that allows users to:
 * 1. Filter hazards from the library by typing (e.g., "Vib" shows "Vibration Exposure")
 * 2. Select a hazard from the filtered list
 * 3. Create custom hazards by typing and pressing Enter
 */
const HazardAutocomplete = ({
  hazardLibrary = [],
  value = '',
  onChange,
  onSelect,
  placeholder = 'Type to search hazards or enter custom...',
  className = '',
  disabled = false
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [isOpen, setIsOpen] = useState(false);
  const [filteredHazards, setFilteredHazards] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Filter hazards based on input
  useEffect(() => {
    if (inputValue.trim().length > 0) {
      const searchTerm = inputValue.toLowerCase();
      const filtered = hazardLibrary.filter(hazard => {
        const nameMatch = hazard.hazard_name?.toLowerCase().includes(searchTerm);
        const categoryMatch = hazard.category?.toLowerCase().includes(searchTerm);
        const descriptionMatch = hazard.hazard_description?.toLowerCase().includes(searchTerm);
        return nameMatch || categoryMatch || descriptionMatch;
      });

      // Remove duplicates based on hazard_name
      const uniqueHazards = filtered.reduce((acc, current) => {
        const exists = acc.find(item => item.hazard_name === current.hazard_name);
        if (!exists) {
          acc.push(current);
        }
        return acc;
      }, []);

      setFilteredHazards(uniqueHazards);
      setIsOpen(uniqueHazards.length > 0 || inputValue.trim().length >= 2);
    } else {
      setFilteredHazards(hazardLibrary);
      setIsOpen(false);
    }
    setHighlightedIndex(-1);
  }, [inputValue, hazardLibrary]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange?.(newValue);
  };

  const handleSelectHazard = (hazard) => {
    setInputValue(hazard.hazard_name);
    setIsOpen(false);
    onSelect?.(hazard);
  };

  const handleCreateCustom = () => {
    if (inputValue.trim()) {
      // Create a custom hazard object
      const customHazard = {
        id: `custom-${Date.now()}`,
        category: 'Custom',
        hazard_name: inputValue.trim(),
        hazard_description: inputValue.trim(),
        default_consequences: '',
        suggested_controls: [],
        severity_guidance: 3,
        isCustom: true
      };
      setIsOpen(false);
      onSelect?.(customHazard);
    }
  };

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < filteredHazards.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredHazards.length) {
          handleSelectHazard(filteredHazards[highlightedIndex]);
        } else if (inputValue.trim()) {
          handleCreateCustom();
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleFocus = () => {
    if (inputValue.trim().length > 0 || hazardLibrary.length > 0) {
      setIsOpen(true);
    }
  };

  // Check if current input matches any existing hazard
  const isExistingHazard = filteredHazards.some(
    h => h.hazard_name?.toLowerCase() === inputValue.trim().toLowerCase()
  );

  const showCustomOption = inputValue.trim().length >= 2 && !isExistingHazard;

  return (
    <div className={`relative ${className}`}>
      {/* Input Field */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          placeholder={placeholder}
          disabled={disabled}
          className="w-full px-4 py-2 pr-10 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white"
          autoComplete="off"
        />
        {/* Dropdown Arrow */}
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          tabIndex={-1}
        >
          <svg
            className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto"
        >
          {/* Custom Hazard Option */}
          {showCustomOption && (
            <div
              onClick={handleCreateCustom}
              className="px-4 py-3 cursor-pointer bg-blue-50 hover:bg-blue-100 border-b border-gray-200"
            >
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                <span className="text-blue-700 font-medium">
                  Create custom hazard: "{inputValue.trim()}"
                </span>
              </div>
              <p className="text-xs text-blue-600 mt-1 ml-7">Press Enter or click to add</p>
            </div>
          )}

          {/* Filtered Hazard Options */}
          {filteredHazards.length > 0 ? (
            filteredHazards.map((hazard, index) => (
              <div
                key={hazard.id}
                onClick={() => handleSelectHazard(hazard)}
                className={`px-4 py-2 cursor-pointer transition-colors ${
                  index === highlightedIndex
                    ? 'bg-green-100'
                    : 'hover:bg-gray-100'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-800">{hazard.hazard_name}</span>
                  <span className="text-xs px-2 py-1 bg-gray-200 text-gray-600 rounded">
                    {hazard.category}
                  </span>
                </div>
                {hazard.hazard_description && (
                  <p className="text-xs text-gray-500 mt-1 line-clamp-1">
                    {hazard.hazard_description}
                  </p>
                )}
              </div>
            ))
          ) : (
            !showCustomOption && inputValue.trim().length > 0 && (
              <div className="px-4 py-3 text-gray-500 text-center">
                <p>No matching hazards found</p>
                <p className="text-xs mt-1">Type at least 2 characters to create a custom hazard</p>
              </div>
            )
          )}

          {/* Show all option when focused with empty input */}
          {inputValue.trim().length === 0 && hazardLibrary.length > 0 && (
            <>
              <div className="px-4 py-2 text-xs text-gray-500 bg-gray-50 border-b">
                Type to filter or select from {hazardLibrary.length} standard hazards
              </div>
              {hazardLibrary.slice(0, 10).map((hazard, index) => (
                <div
                  key={hazard.id}
                  onClick={() => handleSelectHazard(hazard)}
                  className={`px-4 py-2 cursor-pointer transition-colors ${
                    index === highlightedIndex
                      ? 'bg-green-100'
                      : 'hover:bg-gray-100'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-800">{hazard.hazard_name}</span>
                    <span className="text-xs px-2 py-1 bg-gray-200 text-gray-600 rounded">
                      {hazard.category}
                    </span>
                  </div>
                </div>
              ))}
              {hazardLibrary.length > 10 && (
                <div className="px-4 py-2 text-xs text-gray-500 text-center border-t">
                  ... and {hazardLibrary.length - 10} more. Start typing to filter.
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default HazardAutocomplete;
